/**
 * MediaButtons Component
 * Graffetta: foto/video, documenti, posizione, contatto, sondaggio.
 * I file scelti NON partono subito: vanno nell'anteprima (didascalia, piu' file insieme).
 * Le voci che il numero attivo non supporta non compaiono (chatCapabilities).
 */

import { memo, useRef, useState } from 'react';
import { Image, FileText, Paperclip, X, MapPin, BarChart2, UserSquare2 } from 'lucide-react';
import { LocationModal } from './LocationModal';
import { PollModal } from './PollModal';
import { ContactPickerModal } from './ContactPickerModal';
import { DOCUMENT_ACCEPT, filesFromList } from './attachments';
import { iconTip } from '@/components/messages/ChatTooltip';
import type { ChatCapabilities } from '@/lib/services/whatsapp/chatCapabilities';

interface MediaButtonsProps {
  onFilesSelected: (files: File[]) => void;
  isOpen: boolean;
  onToggle: () => void;
  /** Non piu' usato (la posizione va sempre nella chat aperta), resta per compatibilita'. */
  chatId?: string;
  capabilities: Pick<ChatCapabilities, 'canSendPoll' | 'canSendLocation' | 'canSendContact'>;
  /** Allegati, posizione e contatto non inviabili ora (es. finestra di 24 ore chiusa): spiegazione nel tooltip. */
  disabledReason?: string;
  /** Click sulla graffetta quando disabledReason e' impostato (es. apre i modelli approvati). */
  onBlockedClick?: () => void;
}

export const MediaButtons = memo(function MediaButtons({
  onFilesSelected,
  isOpen,
  onToggle,
  capabilities,
  disabledReason,
  onBlockedClick,
}: MediaButtonsProps) {
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);
  const [showLocation, setShowLocation] = useState(false);
  const [showPoll, setShowPoll] = useState(false);
  const [showContact, setShowContact] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = filesFromList(e.target.files);
    if (files.length > 0) {
      onFilesSelected(files);
      if (isOpen) onToggle();
    }
    e.target.value = '';
  };

  const blocked = !!disabledReason;
  const item = 'w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors text-left';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          if (blocked) { if (isOpen) onToggle(); onBlockedClick?.(); return; }
          onToggle();
        }}
        className={`p-1.5 rounded-md transition-colors ${blocked ? 'opacity-40 cursor-not-allowed text-[#A4A4A0]' : isOpen ? 'bg-[rgba(55,53,47,0.08)] dark:bg-white/10 text-[#787774]' : 'hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] text-[#A4A4A0] hover:text-[#787774] dark:hover:text-white/70'}`}
        aria-expanded={isOpen && !blocked}
        aria-disabled={blocked || undefined}
        {...iconTip(blocked ? disabledReason! : isOpen ? 'Chiudi' : 'Allega foto, video, documenti, posizione o contatto. Puoi anche trascinare i file nella chat o incollare un\'immagine')}
      >
        {isOpen && !blocked ? <X className="w-4 h-4" /> : <Paperclip className="w-4 h-4" />}
      </button>

      {isOpen && !blocked && (
        <div className="absolute bottom-12 left-0 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.09)] dark:border-white/10 p-2 space-y-1 min-w-[180px]">
          <button type="button" onClick={() => mediaInputRef.current?.click()} className={item}>
            <Image className="w-5 h-5 text-[#2383E2]" />
            <span className="text-sm text-[#37352F] dark:text-white">Foto e video</span>
          </button>

          <button type="button" onClick={() => documentInputRef.current?.click()} className={item}>
            <FileText className="w-5 h-5 text-orange-500" />
            <span className="text-sm text-[#37352F] dark:text-white">Documento</span>
          </button>

          {(capabilities.canSendLocation || capabilities.canSendContact || capabilities.canSendPoll) && (
            <div className="h-px bg-[rgba(55,53,47,0.09)] dark:bg-white/10 my-1" />
          )}

          {capabilities.canSendLocation && (
            <button type="button" onClick={() => { setShowLocation(true); onToggle(); }} className={item}>
              <MapPin className="w-5 h-5 text-green-500" />
              <span className="text-sm text-[#37352F] dark:text-white">Posizione</span>
            </button>
          )}

          {capabilities.canSendContact && (
            <button type="button" onClick={() => { setShowContact(true); onToggle(); }} className={item}>
              <UserSquare2 className="w-5 h-5 text-sky-500" />
              <span className="text-sm text-[#37352F] dark:text-white">Contatto</span>
            </button>
          )}

          {capabilities.canSendPoll && (
            <button type="button" onClick={() => { setShowPoll(true); onToggle(); }} className={item}>
              <BarChart2 className="w-5 h-5 text-pink-500" />
              <span className="text-sm text-[#37352F] dark:text-white">Sondaggio</span>
            </button>
          )}
        </div>
      )}

      {/* Se la finestra si chiude con un modale aperto, il modale sparisce: l'invio verrebbe rifiutato. */}
      {showLocation && !blocked && <LocationModal onClose={() => setShowLocation(false)} />}
      {showPoll && !blocked && <PollModal onClose={() => setShowPoll(false)} />}
      {showContact && !blocked && <ContactPickerModal onClose={() => setShowContact(false)} />}

      {/* Input nascosti: piu' file insieme */}
      <input ref={mediaInputRef} type="file" multiple accept="image/*,video/*" className="hidden" onChange={handleFileChange} />
      <input ref={documentInputRef} type="file" multiple accept={DOCUMENT_ACCEPT} className="hidden" onChange={handleFileChange} />
    </div>
  );
});
